import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {ElementRef, Inject, Injectable, Renderer2, RendererFactory2, DOCUMENT} from '@angular/core';
import { ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';
import {
  BehaviorSubject,
  firstValueFrom,
  lastValueFrom, Observable,
  of as observableOf,
  ReplaySubject,
  Subscription,
  throwError as observableThrowError
} from 'rxjs';
import {catchError, delay, take} from 'rxjs/operators';
import {defer, isEmpty, isNaN} from 'lodash';
import {delay as _delay} from 'lodash';
import { ApiService } from './api.service';
import { ErrorHistoryService } from './error-history.service';
import { LoggerService } from './logger.service';
import { HTTP_CLIENT } from './token.service';
import {FeatureService} from "./feature.service";


import {
  BRANDING_TIMESTAMP_KEY,
  LOGO_TIMESTAMP_KEY,
  OrganizationPreferencesService
} from "./organization-preferences.service";

import {DomSanitizer, SafeUrl} from "@angular/platform-browser";

@Injectable({ providedIn: 'root' })
export class BrandingModeService {
    public brandingAvailable = false;
    public _darkModeEnabled = false;
    public tenantLabel;
    public domain;
    public logosAvailable;
    public logoCss;
    private _theme = 'light';
    private _primary = '#025cc9';
    private _secondary = '#08dc5a';
    private currentOrg: any = {};
    private currentTenant: any = {};
    private currentRealm: any = {};
    private darkModeSetting = '';

    public defaultTheme;
    public defaultPrimary;
    public defaultSecondary;
    public defaultPrimaryDark;
    public defaultSecondaryDark;
    public defaultLogoLight;
    public defaultLogoDark;
    public defaultBrandCSS;
    public defaultPrimaryOpaque;
    public defaultSecondaryOpaque;
    public defaultPrimaryOpaqueDark;
    public defaultSecondaryOpaqueDark;
    public defaultRealmTheme;
    public defaultRealmPrimary;
    public defaultRealmSecondary;
    public defaultRealmPrimaryDark;
    public defaultRealmSecondaryDark;
    public defaultRealmLogoLight;
    public defaultRealmLogoDark;
    private data = new ReplaySubject<boolean>();
    public brandingUpdates = this.data.asObservable();
    private s3BucketPrefix = 'nf-console-branding-';
    public orgBrandingInit = false;
    public loginBrandingCSS = '';
    public lightModeStyleSheetLink: SafeUrl;
    public darkModeStyleSheetLink: SafeUrl;

    public realmBranding: BehaviorSubject<any> = new BehaviorSubject<any>({});
    public currentBranding: BehaviorSubject<any> = new BehaviorSubject<any>({});

    identityApiUrl = '';
    brandingUpdatedSource = new ReplaySubject<boolean>(1);
    brandingUpdated = this.brandingUpdatedSource.asObservable();
    renderer: Renderer2;

    subscription = new Subscription();

    constructor(
        protected logger: LoggerService,
        @Inject(ENVIRONMENT) private environment: Environment,
        @Inject(HTTP_CLIENT) public httpClient: HttpClient,
        private apiService: ApiService,
        private errorHistoryService: ErrorHistoryService,
        private featureService: FeatureService,
        private orgPreferencesService: OrganizationPreferencesService,
        private rendererFactory: RendererFactory2,
        private sanitizer: DomSanitizer,
        @Inject(DOCUMENT) private document: Document
    ) {
      this.renderer = this.rendererFactory.createRenderer(null, null);
      this.subscription.add(this.apiService.currentRealm.subscribe((realm) => {
        this.currentRealm = realm;
        if (!realm?.id) {
          return;
        }
        this.initRealmBranding();
      }));
      this.subscription.add(this.apiService.currentTenant.subscribe((tenant) => {
        this.currentTenant = tenant;
      }));
      this.subscription.add(this.apiService.currentOrganization.subscribe((org) => {
        this.currentOrg = org;
        if (!org?.id) {
          return;
        }
        this.initOrgBranding(org);
      }));
      this.subscription.add(this.featureService.darkModeSetting.subscribe((darkModeSetting) => {
        this.darkModeSetting = darkModeSetting;
        if (!this.environment.realmsSupported || isEmpty(this.currentOrg?.id)) {
          return;
        }
        defer(() => {
          if (this.featureService.darkModeUnset) {
            this.appendOrgBrandingStylesToDOM(true);
          } else {
            this.appendOrgBrandingStylesToDOM(false);
          }
        })
      }));
      this.identityApiUrl = environment?.identityConfig?.url;
    }

    get isDarkModeEnabled() {
      if (this.darkModeSetting === 'auto') {
        const systemDarkmodeEnabled = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        return systemDarkmodeEnabled;
      }
      return this.darkModeSetting === 'true' || this.featureService.darkModeEnabled === true;
    }

    initRealmBranding() {
      if (!this.environment.realmsSupported) {
        return;
      }
      const currentRealm = this.apiService.currentRealm?.value;
      this.loadBrandingStylesheets(currentRealm?.id).then((brandingData: any) => {
        this.defaultRealmPrimary = brandingData?.primaryColor;
        this.defaultRealmSecondary = brandingData?.secondaryColor;
        this.defaultRealmPrimaryDark = brandingData?.darkPrimaryColor;
        this.defaultRealmSecondaryDark = brandingData?.darkSecondaryColor;
        this.defaultRealmLogoLight = brandingData?.lightLogo;
        this.defaultRealmLogoDark = brandingData?.darkLogo;
        this.defaultRealmTheme = brandingData?.defaultTheme;
        this.removeStyleBlock('dark-mode-stylesheet');
        if (this.featureService.darkModeUnset && this.defaultRealmTheme === 'dark') {
          return this.loadDarkmodeStylesheets().then((styleSheet) => {
            this.appendStyleBlock(styleSheet, 'dark-mode-stylesheet');
            this.appendRealmBrandingStylesToDOM(true);
          });
        } else {
          this.appendRealmBrandingStylesToDOM(false);
        }
      });
    }

    initOrgBranding(org?) {
      if (!this.environment.realmsSupported) {
        return;
      }
      this.loadBrandingStylesheets(undefined, org?.id).then((brandingData: any) => {
        this.defaultPrimary = !isEmpty(brandingData?.primaryColor) ? brandingData?.primaryColor : this.defaultRealmPrimary;
        this.defaultSecondary = !isEmpty(brandingData?.secondaryColor) ? brandingData?.secondaryColor : this.defaultRealmSecondary;
        this.defaultPrimaryDark = !isEmpty(brandingData?.darkPrimaryColor) ? brandingData?.darkPrimaryColor : this.defaultRealmPrimaryDark;
        this.defaultSecondaryDark = !isEmpty(brandingData?.darkSecondaryColor) ? brandingData?.darkSecondaryColor : this.defaultRealmSecondary;
        this.defaultLogoLight = !isEmpty(brandingData?.lightLogo) ? brandingData?.lightLogo : this.defaultRealmLogoLight;
        this.defaultLogoDark = !isEmpty(brandingData?.darkLogo) ? brandingData?.darkLogo : this.defaultRealmLogoDark;
        this.defaultTheme = !isEmpty(brandingData?.defaultTheme) ? brandingData?.defaultTheme : this.defaultRealmTheme;
        this.removeStyleBlock('dark-mode-stylesheet');
        this.currentBranding.next(brandingData);
        if (this.featureService.darkModeUnset && this.defaultTheme === 'dark') {
          return this.loadDarkmodeStylesheets().then((styleSheet) => {
            this.appendStyleBlock(styleSheet, 'dark-mode-stylesheet');
            this.appendOrgBrandingStylesToDOM(true);
          });
        } else {
          this.appendOrgBrandingStylesToDOM(false);
        }
      });
    }

    loadBrandingStylesheets(realmId?, orgId?) {
        return firstValueFrom(this.getOrganizationBranding(realmId, orgId));
    }

    loadDarkmodeStylesheets(): Promise<any> {
      const url = window.location.origin + '/assets/styles/darkmode.css';
      const cacheBuster = `?v=${new Date().getTime()}`;
      const fullUrl = url + cacheBuster;

      // 2. Send the request
      return firstValueFrom(this.httpClient.get(fullUrl, {
        // 3. IMPORTANT: Specify the response type as 'text'
        responseType: 'text'
      }));
    }

    getOrgStyleBlock(realmId?, orgId?) {
      return firstValueFrom(this.getOrganizationBranding(realmId, orgId)).then((result: any) => {
        let styleBlock = '';
        const brandingTimestamp = Date.now();
        if (result.defaultTheme === 'dark') {
          const darkPrimaryColor = !isEmpty(result.darkPrimaryColor) ? result.darkPrimaryColor : this.defaultRealmPrimaryDark;
          const darkSecondaryColor = !isEmpty(result.darkSecondaryColor) ? result.darkSecondaryColor : this.defaultRealmSecondaryDark;
          const primaryColor = !isEmpty(result.primaryColor) ? result.primaryColor : this.defaultRealmPrimary;
          const secondaryColor = !isEmpty(result.secondaryColor) ? result.secondaryColor : this.defaultRealmSecondary;
          const darkLogo = !isEmpty(result.darkLogo) ? result.darkLogo : this.defaultRealmLogoDark;
          const defaultLogoDark = !isEmpty(result.darkLogo) ? result.darkLogo : result.lightLogo;
          const defaultLogoLight = !isEmpty(result.lightLogo) ? result.lightLogo : result.darkLogo;
          styleBlock += `
          --primaryColor: ${darkPrimaryColor};
          --secondaryColor: ${darkSecondaryColor};
          --primary: ${darkPrimaryColor};
          --secondary: ${darkSecondaryColor};
          --primaryColorDark: ${darkPrimaryColor};
          --secondaryColorDark: ${darkSecondaryColor};
          --primaryColorLight: ${primaryColor};
          --secondaryColorLight: ${secondaryColor};
          --primaryColorOpaque: ${darkPrimaryColor}63;
          --secondaryColorOpaque: ${darkSecondaryColor}63;
          --primaryColorOpaqueDark: ${darkPrimaryColor}63;
          --secondaryColorOpaqueDark: ${darkSecondaryColor}63;
          --primaryColorOpaqueLight: ${primaryColor}63;
          --secondaryColorOpaqueLight: ${secondaryColor}63;
          --loginLogo: url(${darkLogo}?v=${brandingTimestamp});
          --navLogo: url(${darkLogo}?v=${brandingTimestamp});
          --logoLight: url(${defaultLogoLight}?v=${brandingTimestamp});
          --logoDark: url(${defaultLogoDark}?v=${brandingTimestamp});
          --backgroundColor: var(--backgroundColor_DARK);
          --controlBoxBackground: var(--controlBoxBackground_DARK);
          --brightness: var(--brightness_DARK);
          --text: var(--text_DARK);
          --inputBackground: var(--inputBackground_DARK);
          --inputBorder: var(--inputBorder_DARK);
          --buttonBackground: var(--buttonBackground_DARK);
        `;
        } else {
          const primaryColor = !isEmpty(result.primaryColor) ? result.primaryColor : this.defaultRealmPrimary;
          const secondaryColor = !isEmpty(result.secondaryColor) ? result.secondaryColor : this.defaultRealmSecondary;
          const lightLogo = !isEmpty(result.lightLogo) ? result.lightLogo : this.defaultRealmLogoLight;
          const darkPrimaryColor = !isEmpty(result.darkPrimaryColor) ? result.darkPrimaryColor : this.defaultRealmPrimaryDark;
          const darkSecondaryColor = !isEmpty(result.darkSecondaryColor) ? result.darkSecondaryColor : this.defaultRealmSecondaryDark;

          const defaultLogoDark = !isEmpty(result.darkLogo) ? result.darkLogo : this.defaultRealmLogoDark;
          const defaultLogoLight = !isEmpty(result.lightLogo) ? result.lightLogo : this.defaultRealmLogoLight;
          styleBlock += `
          --primaryColor: ${primaryColor};
          --secondaryColor: ${secondaryColor};
          --primary: ${primaryColor};
          --secondary: ${secondaryColor};
          --primaryColorDark: ${darkPrimaryColor};
          --secondaryColorDark: ${darkSecondaryColor};
          --primaryColorLight: ${primaryColor};
          --secondaryColorLight: ${secondaryColor};
          --primaryColorOpaque: ${primaryColor}63;
          --secondaryColorOpaque: ${secondaryColor}63;
          --primaryColorOpaqueDark: ${darkPrimaryColor}63;
          --secondaryColorOpaqueDark: ${darkSecondaryColor}63;
          --primaryColorOpaqueLight: ${primaryColor}63;
          --secondaryColorOpaqueLight: ${secondaryColor}63;
          --navLogo: url(${lightLogo}?v=${brandingTimestamp});
          --loginLogo: url(${lightLogo}?v=${brandingTimestamp});
          --logoLight: url(${defaultLogoLight}?v=${brandingTimestamp});
          --logoDark: url(${defaultLogoDark}?v=${brandingTimestamp});
        `;
        }
        const defaultBorderRadius = this.getStyleVariableValue('--defaultBorderRadius');
        if (defaultBorderRadius) {
            styleBlock += ` --defaultBorderRadius: ${defaultBorderRadius}; --inputBorderRadius: ${defaultBorderRadius};`
        }
        const defaultInputHeight = this.getStyleVariableValue('--defaultInputHeight');
        if (defaultBorderRadius) {
            styleBlock += ` --defaultInputHeight: ${defaultInputHeight};`
        }
        if (this.featureService.isCloudZiti) {
            styleBlock += ` --defaultFontSize: 90%;`
        }
        return styleBlock;
      }).catch((error) => {
        return {};
      });
    }

    // Structured branding for the ACUL login bundle: same isEmpty()->defaultRealm* fallbacks
    // getOrgStyleBlock() applies, so a partially-configured org degrades to realm defaults
    // instead of losing branding. Undefined fields are dropped by JSON.stringify downstream.
    resolveBrandingWithRealmFallbacks(raw: any = {}) {
      const r = raw || {};
      return {
        primaryColor: !isEmpty(r.primaryColor) ? r.primaryColor : this.defaultRealmPrimary,
        secondaryColor: !isEmpty(r.secondaryColor) ? r.secondaryColor : this.defaultRealmSecondary,
        darkPrimaryColor: !isEmpty(r.darkPrimaryColor) ? r.darkPrimaryColor : this.defaultRealmPrimaryDark,
        darkSecondaryColor: !isEmpty(r.darkSecondaryColor) ? r.darkSecondaryColor : this.defaultRealmSecondaryDark,
        lightLogo: !isEmpty(r.lightLogo) ? r.lightLogo : this.defaultRealmLogoLight,
        darkLogo: !isEmpty(r.darkLogo) ? r.darkLogo : this.defaultRealmLogoDark,
        theme: !isEmpty(r.defaultTheme) ? r.defaultTheme : this.defaultRealmTheme,
      };
    }

    appendRealmBrandingStylesToDOM(ignoreFeatureSetting = false) {
      const html = document.getElementsByTagName('body')[0];
      const styleBlock = this.getStyleBlock(this.defaultRealmPrimary, this.defaultRealmSecondary, this.defaultRealmPrimaryDark, this.defaultRealmSecondaryDark, this.defaultRealmLogoLight, this.defaultRealmLogoDark, undefined, undefined, ignoreFeatureSetting);
      this.defaultBrandCSS = styleBlock;
      html.style.cssText = styleBlock;
      if (!this.apiService.realmBrandingInit.value) {
        this.apiService.realmBrandingInit.next(true);
      }
    }

    appendOrgBrandingStylesToDOM(ignoreFeatureSetting = false) {
      const html = document.getElementsByTagName('body')[0];
      const styleBlock = this.getStyleBlock(this.defaultPrimary, this.defaultSecondary, this.defaultPrimaryDark, this.defaultSecondaryDark, this.defaultLogoLight, this.defaultLogoDark, undefined, undefined, ignoreFeatureSetting);
      this.defaultBrandCSS = styleBlock;
      html.style.cssText = styleBlock;
      if (!isEmpty(this.defaultLogoLight) || !isEmpty(this.defaultLogoDark)) {
        this.logosAvailable = true;
      } else {
        this.logosAvailable = false;
      }
    }

    getStyleBlock(primary, secondary, primaryDark, secondaryDark, logoLight, logoDark, logoLightPreview, logoDarkPreview, ignoreFeatureSetting = false) {
      let styleBlock = '';
      const brandingTimestamp = Date.now();
      if ((ignoreFeatureSetting && ((isEmpty(this.defaultTheme) && this.defaultRealmTheme === 'dark') || this.defaultTheme === 'dark')) || this.isDarkModeEnabled) {
        const defaultPrimaryDark = !isEmpty(primaryDark) ? primaryDark : primary;
        const defaultSecondaryDark = !isEmpty(secondaryDark) ? secondaryDark : secondaryDark;
        const defaultLogoDark = !isEmpty(logoDark) ? logoDark : logoLight;
        const defaultLogoLight = !isEmpty(logoLight) ? logoLight : logoDark;
        styleBlock += `
          --primary: ${defaultPrimaryDark};
          --secondary: ${defaultSecondaryDark};
          --primaryColor: ${defaultPrimaryDark};
          --secondaryColor: ${defaultSecondaryDark};
          --primaryColorDark: ${defaultPrimaryDark};
          --secondaryColorDark: ${defaultSecondaryDark};
          --primaryColorLight: ${primary};
          --secondaryColorLight: ${secondary};
          --primaryColorOpaque: ${defaultPrimaryDark}63;
          --secondaryColorOpaque: ${defaultSecondaryDark}63;
          --primaryColorOpaqueDark: ${defaultPrimaryDark}63;
          --secondaryColorOpaqueDark: ${defaultSecondaryDark}63;
          --primaryColorOpaqueLight: ${primary}63;
          --secondaryColorOpaqueLight: ${secondary}63;
          --navLogo: url(${defaultLogoDark}?v=${brandingTimestamp});
          --loginLogo: url(${defaultLogoDark}?v=${brandingTimestamp});
          --logoLight: url(${defaultLogoLight}?v=${brandingTimestamp});
          --logoDark: url(${defaultLogoDark}?v=${brandingTimestamp});
          --boxShadow: var(--boxShadowDark);
        `;
      } else {
        const defaultLogoDark = !isEmpty(logoDark) ? logoDark : logoLight;
        const defaultLogoLight = !isEmpty(logoLight) ? logoLight : logoDark;
        const defaultPrimaryDark = !isEmpty(primaryDark) ? primaryDark : primary;
        const defaultSecondaryDark = !isEmpty(secondaryDark) ? secondaryDark : secondaryDark;
        styleBlock += `
          --primary: ${primary};
          --secondary: ${secondary};
          --primaryColor: ${primary};
          --secondaryColor: ${secondary};
          --primaryColorDark: ${defaultPrimaryDark};
          --secondaryColorDark: ${defaultSecondaryDark};
          --primaryColorLight: ${primary};
          --secondaryColorLight: ${secondary};
          --primaryColorOpaque: ${primary}63;
          --secondaryColorOpaque: ${secondary}63;
          --primaryColorOpaqueDark: ${defaultPrimaryDark}63;
          --secondaryColorOpaqueDark: ${defaultSecondaryDark}63;
          --primaryColorOpaqueLight: ${primary}63;
          --secondaryColorOpaqueLight: ${secondary}63;
          --navLogo: url(${logoLight}?v=${brandingTimestamp});
          --loginLogo: url(${logoLight}?v=${brandingTimestamp});
          --logoLight: url(${defaultLogoLight}?v=${brandingTimestamp});
          --logoDark: url(${defaultLogoDark}?v=${brandingTimestamp});
        `;
      }
      if (logoLightPreview) {
        styleBlock += `--logoLightPreview: url(${logoLightPreview});`
      } else {
        styleBlock += `--logoLightPreview: var(--logoLight);`
      }
      if (logoDarkPreview) {
        styleBlock += `--logoDarkPreview: url(${logoDarkPreview});`
      } else {
        styleBlock += `--logoDarkPreview: var(--logoDark);`
      }
      return styleBlock;
    }

    storeBrandingColors(realmId?, orgId?, primary?, secondary?, mode = 'Light', defaultTheme?) {
      const colorData: any = {
        organizationId: orgId,
        primaryColor: primary,
        secondaryColor: secondary,
        targetMode: mode
      };
      if (!isEmpty(defaultTheme)) {
        colorData.defaultTheme = defaultTheme;
      }
      return firstValueFrom(this.setOrganizationBrandingColors(colorData)).then((result) => {
        return result;
      }).catch(() => {
        return {};
      });
    }

    storeBrandingLogos(realmId?, orgId?, lightLogoFile?, darkLogoFile?) {
      const colorData = {
        organizationId: orgId,
        lightLogoFile: lightLogoFile,
        darkLogoFile: darkLogoFile,
      };
      const formData: FormData = new FormData();
      if (realmId) {
        formData.append('realmId', realmId);
      }
      if (orgId) {
        formData.append('organizationId', orgId);
      }
      if (lightLogoFile) {
        formData.append('lightLogoFile', lightLogoFile, lightLogoFile?.name);
      }
      if (darkLogoFile) {
        formData.append('darkLogoFile', darkLogoFile, darkLogoFile?.name);
      }

      return firstValueFrom(this.setOrganizationBrandingLogos(formData)).then((result) => {
        return result;
      });
    }

    deleteBrandingLogos(realmId?, orgId?, deleteLight = false, deleteDark = false) {
      const fullpath = `${this.identityApiUrl}nfconsole/branding/logos`;
      const headers = this.setHeaders(true);
      const body = {
        organizationId: orgId
      };
      return this.httpClient
        .delete(fullpath, { headers: headers, body: body })
        .pipe(catchError((error) => this.handleError(this, error)));
    }

    appendRealmStylesheets(cssLinks) {
      const head = this.document.head;
      if (!head) {
        console.error('The <head> element was not found.');
        return;
      }

      cssLinks.forEach(url => {
        // Check if the stylesheet is already loaded to avoid duplicates
        if (!this.document.querySelector(`link[href="${url}"]`)) {
          const link = this.renderer.createElement('link');
          this.renderer.setAttribute(link, 'rel', 'stylesheet');
          this.renderer.setAttribute(link, 'href', url);
          this.renderer.appendChild(head, link);
          console.log(`Successfully loaded stylesheet: ${url}`);
        }
      });
    }

    async fetchBrandCss(tenantLabel?: string): Promise<any> {
        if (this.environment.realmsSupported && !isEmpty(this.defaultBrandCSS)) {
          return Promise.resolve(this.defaultBrandCSS);
        }
        if (!tenantLabel) {
            this.tenantLabel = await firstValueFrom(this.apiService.currentTenant.pipe(catchError((err) => null))).then(
                (tenant: any) => tenant?.label?.toLowerCase(),
                (error) => {
                    this.logosAvailable = false;
                }
            );
            // bail if we're not logged in yet
            if (!this.tenantLabel) {
                this.logosAvailable = false;
                return;
            }
        } else this.tenantLabel = tenantLabel.toLowerCase();

        this.domain = this.environment.domain;

        this._checkDarkMode();
        if (this.environment.domain !== 'localhost' && this.environment.domain !== 'uuix') {
            let brandUrl = `https://s3.amazonaws.com/${this.s3BucketPrefix}${this.domain}/netfoundry/${this.tenantLabel}/css/${this._theme}.css`;
            let brandUrl2 = `https://s3.amazonaws.com/${this.s3BucketPrefix}${this.domain}/${this.tenantLabel}/css/${this._theme}.css`;
            const brandingTimestamp = Date.now();
            if (brandingTimestamp && !isNaN(brandingTimestamp)) {
              brandUrl += `?t=${brandingTimestamp}`;
              brandUrl2 += `?t=${brandingTimestamp}`;
            }

            return this.fetchBrandingFromURL(brandUrl).catch(() => {
              return this.fetchBrandingFromURL(brandUrl2);
            }).finally(() => {
              this.brandingUpdatedSource.next(true);
            });
        } else return Promise.resolve();
    }

    fetchBrandingFromURL(url) {
      // use a service call to get the CSS as text otherwise angular and the browser fire security alarms
      const headers = new HttpHeaders().set('X-Skip-Interceptor', 'true');

      return lastValueFrom(
        this.httpClient.get(url, { headers: headers, responseType: 'text' }).pipe(
          catchError((err) => {
            this.brandingAvailable = false;
            this.logosAvailable = false;
            this.errorHistoryService.addError(err.message);
            this.configureDefaultBranding();
            return null;
          })
        )
      ).then((res) => {
        // strip out CSS brackets so we don't have to do a CORS security bypass
        let rawCss;
        if (typeof res === 'string') {
          rawCss = res.replace(':root {', '').replace('}', '');
        }
        this.logger.info('Inject branding CSS', rawCss);
        rawCss = this.configureDefaultBranding(rawCss);
        this.logger.info('Branding primary color', this._primary);
        this.logger.info('Branding secondary color', this._secondary);
        this.brandingAvailable = true;
        rawCss += `--primary: ${this._primary}; --secondary: ${this._secondary}; --navLogo: var(--logoLight);`;
        // set the logo URL
        // get the logos if we have them
        return this.getLogos(rawCss);
      });
    }

    private configureDefaultBranding(rawCss = '') {
        const parts = rawCss.split(';');
        this._primary = parts[0]?.split(':')[1]?.trim();
        this._secondary = parts[1]?.split(':')[1]?.trim();
        let loginLogo;
        parts.forEach((part) => {
            if (part.split(':')[0]?.trim()?.indexOf('loginLogo') >= 0) {
                loginLogo = part.split(':')[1]?.trim();
            }
        });
        if (!loginLogo) {
          if (this.environment?.defaultBranding?.loginLogo) {
              loginLogo = `url(${window.location.origin + this.environment?.defaultBranding?.loginLogo})`;
          } else {
              loginLogo = 'var(--logoLight)';
          }
        }
        rawCss += `--loginLogo: ${loginLogo}; `;
        if (this.environment.defaultTheme === 'dark') {
            this.loginBrandingCSS = `--backgroundColor: var(--backgroundColor_DARK);
                      --controlBoxBackground: var(--controlBoxBackground_DARK);
                      --brightness: var(--brightness_DARK);
                      --text: var(--text_DARK);
                      --inputBackground: var(--inputBackground_DARK);
                      --inputBorder: var(--inputBorder_DARK);
                      --loginLogo: ${loginLogo};
                      --buttonBackground: var(--buttonBackground_DARK);
                      `;
        }
        return rawCss;
    }

    private getLogos(rawCss): Promise<any> {
        let logoCssUrl = `https://s3.amazonaws.com/${this.s3BucketPrefix}${this.domain}/${this.tenantLabel}/css/logo.css`;
        const headers = new HttpHeaders().set('X-Skip-Interceptor', 'true');
        const logoTimestamp = this.orgPreferencesService.getOrganizationPreference(LOGO_TIMESTAMP_KEY);
        if (logoTimestamp && !isNaN(logoTimestamp)) {
          logoCssUrl += `?t=${logoTimestamp}`;
        }
        return lastValueFrom(
            this.httpClient.get(logoCssUrl, { headers: headers, responseType: 'text' }).pipe(
                catchError((err) => {
                    //html.style.cssText = rawCss;
                    this.logosAvailable = false;
                    this.sendBrandingUpdate(true);
                    return rawCss;
                })
            )
        ).then((res) => {
            let logoCss;
            if (typeof res === 'string') {
                logoCss = res.replace(':root {', '').replace('}', '');
            }
            this.logger.info('Inject Logos', logoCss);

            // wait until both requests are complete to inject the CSS
            //html.style.cssText = rawCss + logoCss;
            this.logoCss = logoCss;
            this.logosAvailable = true;
            this.sendBrandingUpdate(true);
            return rawCss + logoCss;
        });
    }

    // This is the restore called upon a logout, to persist the color restore, need to do that from the branding component
    public restore() {
        if (this.featureService.isCloudZiti) {
          this.initRealmBranding();
          return;
        }
        this._primary = this.defaultPrimary || '#025cc9';
        this._secondary = this.defaultSecondary || '#08dc5a';
        const html = document.getElementsByTagName('body')[0];
        html.style.cssText = '';
    }

    getPrimary() {
        return this._primary;
    }

    setPrimary(primary) {
        this._primary = primary;
    }

    getSecondary() {
        return this._secondary;
    }

    setSecondary(secondary) {
      this._secondary = secondary;
    }

    private sendBrandingUpdate(data: boolean) {
        this.data.next(data);
    }

    get darkModeEnabled() {
        return this._darkModeEnabled === true || this.featureService.darkModeEnabled ;
    }

    set darkModeEnabled(enabled) {
        this._darkModeEnabled = enabled;
        this.featureService.darkModeEnabled = enabled;
    }

    public updateDarkMode() {
        this._darkModeEnabled = this.featureService.darkModeEnabled;
    }

    public initCustomBrandingUrl() {
      if (!this.environment.defaultBranding || this.environment.realmsSupported) {
        return;
      }
      this.defaultTheme = this.environment.defaultTheme;
      this.defaultPrimary = this.environment.defaultBranding.primaryColor;
      this.defaultSecondary = this.environment.defaultBranding.secondaryColor;
      this.defaultPrimaryOpaque = this.environment.defaultBranding.primaryColorOpaque;
      this.defaultSecondaryOpaque = this.environment.defaultBranding.secondaryColorOpaque;
      this.defaultBrandCSS = `
        --primaryColor: ${this.defaultPrimary};
        --secondaryColor: ${this.defaultSecondary};
        --primaryColorOpaque: ${this.defaultPrimaryOpaque};
        --secondaryColorOpaque: ${this.defaultSecondaryOpaque};
        --loginLogo: url(${window.origin + this.environment.defaultBranding.loginLogo});
      `;
    }

    private _checkDarkMode() {
        try {
          const localSetting = localStorage.getItem('darkMode_' + this.tenantLabel);
          if (localSetting === 'true') {
            this._theme = 'dark';
            this.darkModeEnabled = true;
            this.logger.info('Dark mode is enabled');
          }
        } catch(err) {
          this.logger.error('failed to update dark mode setting: ' + err);
        }
    }

    appendStyleBlock(cssContent: string, id: string): void {
      const style: HTMLStyleElement = this.renderer.createElement('style');
      this.renderer.setAttribute(style, 'id', id);

      const text = this.renderer.createText(cssContent);
      this.renderer.appendChild(style, text);

      const head = this.document.getElementsByTagName('head')[0];
      this.renderer.appendChild(head, style);
    }

    removeStyleBlock(id: string): void {
      const style = this.document.getElementById(id);
      if (style) {
        const head = this.document.getElementsByTagName('head')[0];
        this.renderer.removeChild(head, style);
      }
    }

    public getOrganizationBranding(realmId?: string, orgId?: string) {
      let fullpath = `${this.identityApiUrl}nfconsole/branding?`;

      if (!isEmpty(orgId)) {
        fullpath += `organizationId=${orgId}&`;
      }
      if (!isEmpty(realmId)) {
        fullpath += `realmId=${realmId}`;
      }

      // getting the headers. This now checks to determine if the user's token expired
      const headers = this.setHeaders();

      // headers will be not null if the token is valid. If this is the case, kick off the request
      if (headers != null) {
        return this.httpClient
          .get(fullpath, { headers: headers })
          .pipe(catchError((error) => this.handleError(this, error)));
      } else {
        // delaying the response to allow the console time to trigger the logout and terminate the subscription
        return observableOf([]).pipe(delay(1000));
      }
    }

    public setOrganizationBrandingColors(colorData: any = {}) {
      const fullpath = `${this.identityApiUrl}nfconsole/branding/colors?`;

      // getting the headers. This now checks to determine if the user's token expired
      const headers = this.setHeaders();

      // headers will be not null if the token is valid. If this is the case, kick off the request
      if (headers != null) {
        return this.httpClient
          .post(fullpath, colorData, { headers: headers })
          .pipe(catchError((error) => this.handleError(this, error)));
      } else {
        // delaying the response to allow the console time to trigger the logout and terminate the subscription
        return observableOf([]).pipe(delay(1000));
      }
    }

    public setOrganizationBrandingLogos(logoData: any = {}) {
      const fullpath = `${this.identityApiUrl}nfconsole/branding/logos`;
      const headers = this.setHeaders(true);
      return this.httpClient
        .post(fullpath, logoData, { headers: headers })
        .pipe(catchError((error) => this.handleError(this, error)));
    }

    getImageAsBlob(url: string): Observable<Blob> {
      let headers = new HttpHeaders();
      headers = headers.append('Access-Control-Allow-Origin', 'http://sinec.localhost:4200');
      return this.httpClient.get(url, {
        headers: headers,
        responseType: 'blob' // Essential for binary data
      }).pipe(catchError((error) => this.handleError(this, error)));
    }

    getStyleVariableValue(variableName: string): string {
        const rootStyles = getComputedStyle(this.document?.documentElement);
        return rootStyles?.getPropertyValue(variableName)?.trim();
    }

    private setHeaders(skipContentType = false): HttpHeaders {
      const headers = new HttpHeaders();
      if (headers != null && !skipContentType) {
        return headers.append('Content-Type', 'application/json');
      } else {
        return headers;
      }
    }

    private handleError(scope: any, error: HttpErrorResponse) {
      if (error.error instanceof ErrorEvent) {
        scope.logger.error('IAM Service error occurred:', error.error);
      } else {
        scope.logger.error(`IAM Backend returned code ${error.status}`, error.error);
      }
      scope.lastErrorSource.next(error);
      return observableThrowError(error);
    }
}
